/**
 * Resize and re-encode crash photos as JPEG before offline storage, sync, or ZIP export.
 */

export const DEFAULT_MAX_CRASH_IMAGE_PX = 1920
export const DEFAULT_JPEG_QUALITY = 0.82

const IMAGE_DATA_URL_PREFIX = 'data:image/'

/**
 * @param {unknown} value
 * @returns {value is string}
 */
export function isImageDataUrl(value) {
  return typeof value === 'string' && value.startsWith(IMAGE_DATA_URL_PREFIX)
}

/**
 * SVG and already-small payloads are left unchanged by policy.
 * @param {string} dataUrl
 */
export function shouldCompressImageDataUrl(dataUrl) {
  if (!isImageDataUrl(dataUrl)) return false
  if (dataUrl.startsWith('data:image/svg')) return false
  return true
}

/**
 * @param {number} width
 * @param {number} height
 * @param {number} maxDimension
 */
export function scaleToMaxDimension(width, height, maxDimension) {
  if (width <= 0 || height <= 0) {
    return { width: 0, height: 0 }
  }
  const longest = Math.max(width, height)
  if (longest <= maxDimension) {
    return { width, height }
  }
  const ratio = maxDimension / longest
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  }
}

/**
 * @param {string} dataUrl
 * @param {{ maxDimension?: number, quality?: number }} [options]
 * @returns {Promise<string>}
 */
export async function compressCrashImageDataUrl(dataUrl, options = {}) {
  if (!shouldCompressImageDataUrl(dataUrl)) {
    return dataUrl
  }

  if (
    typeof process !== 'undefined' &&
    process.env.JEST_WORKER_ID !== undefined
  ) {
    return dataUrl
  }

  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return dataUrl
  }

  const maxDimension = options.maxDimension ?? DEFAULT_MAX_CRASH_IMAGE_PX
  const quality = options.quality ?? DEFAULT_JPEG_QUALITY
  const loadTimeoutMs = options.loadTimeoutMs ?? 8000

  return new Promise((resolve) => {
    const img = new Image()
    const timeoutId = setTimeout(() => resolve(dataUrl), loadTimeoutMs)
    img.onload = () => {
      clearTimeout(timeoutId)
      try {
        const { width, height } = scaleToMaxDimension(
          img.naturalWidth || img.width,
          img.naturalHeight || img.height,
          maxDimension,
        )
        if (width === 0 || height === 0) {
          resolve(dataUrl)
          return
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(dataUrl)
          return
        }
        ctx.drawImage(img, 0, 0, width, height)
        const compressed = canvas.toDataURL('image/jpeg', quality)
        resolve(compressed || dataUrl)
      } catch {
        resolve(dataUrl)
      }
    }
    img.onerror = () => {
      clearTimeout(timeoutId)
      resolve(dataUrl)
    }
    img.src = dataUrl
  })
}

/**
 * @param {unknown[]} images
 * @param {{ maxDimension?: number, quality?: number }} [options]
 * @returns {Promise<string[]>}
 */
export async function compressCrashImages(images, options = {}) {
  if (!Array.isArray(images) || images.length === 0) {
    return []
  }

  return Promise.all(
    images.map(async (imageValue) => {
      if (typeof imageValue !== 'string' || imageValue.length === 0) {
        return imageValue
      }
      if (!shouldCompressImageDataUrl(imageValue)) {
        return imageValue
      }
      return compressCrashImageDataUrl(imageValue, options)
    }),
  )
}

/**
 * @param {object} payload
 * @param {{ maxDimension?: number, quality?: number }} [options]
 * @returns {Promise<object>}
 */
export async function compressImagesInCrashPayload(payload, options = {}) {
  if (!payload || typeof payload !== 'object') {
    return payload
  }
  const images = payload.crash?.images
  if (!Array.isArray(images) || images.length === 0) {
    return payload
  }

  const compressedImages = await compressCrashImages(images, options)
  return {
    ...payload,
    crash: {
      ...payload.crash,
      images: compressedImages,
    },
  }
}
